/**
 * Overview panel — landing-page metric cards, baseline-vs-governed
 * narrative, and a compact bridge-matrix summary keyed by generator.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GENERATOR_CODES } from "@csk/core";
import { METRICS, GENERATOR_COLORS } from "../constants";
import {
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchFactGrid,
  DemoWorkbenchInset,
  DemoWorkbenchItemList,
  DemoWorkbenchMetricCard,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
} from "@csk/demo-ui";
import type { Bridge } from "../types";

export function OverviewPanel({
  matrixSummary,
  byGenerator,
}: {
  matrixSummary: { allowed: number; forbidden: number };
  byGenerator: Map<string, Bridge[]>;
}): React.JSX.Element {
  return (
    <DemoWorkbenchStack>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <DemoWorkbenchMetricCard
          label="Agda modules"
          value={METRICS.agdaModules}
          sub="zero holes"
          color="text-emerald-400"
        />
        <DemoWorkbenchMetricCard
          label="Tests"
          value={METRICS.testsPassed}
          sub="all passing"
          color="text-teal-400"
        />
        <DemoWorkbenchMetricCard
          label="Engine"
          value={METRICS.engineFunctions}
          sub="functions"
          color="text-amber-400"
        />
        <DemoWorkbenchMetricCard
          label="Theorems"
          value={METRICS.theorems}
          sub="proved"
          color="text-violet-400"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <DemoWorkbenchCard
          title="Baseline executor"
          className="border-red-900/30"
        >
          <DemoWorkbenchCopy>
            Parses tool call from untrusted text. Executes HTTP exfiltration.
            <span className="text-red-400 font-semibold ml-1">
              Succeeds in 600ms.
            </span>
          </DemoWorkbenchCopy>
        </DemoWorkbenchCard>
        <DemoWorkbenchCard
          title="CSK governed executor"
          className="border-emerald-900/30"
        >
          <DemoWorkbenchCopy>
            Bridge matrix check: G2→G1 constructor absent.
            <span className="text-emerald-400 font-semibold ml-1">
              Structural REFUSE in &lt;2ms.
            </span>
          </DemoWorkbenchCopy>
        </DemoWorkbenchCard>
      </div>

      <DemoWorkbenchCard
        title="Bridge matrix"
        right={
          <DemoWorkbenchPill variant="info">
            Allowed: {matrixSummary.allowed} · Forbidden:{" "}
            {matrixSummary.forbidden}
          </DemoWorkbenchPill>
        }
      >
        <DemoWorkbenchFactGrid minColumnWidth={140}>
          {GENERATOR_CODES.map((g) => {
            const list = byGenerator.get(g) ?? [];
            const color = GENERATOR_COLORS[g];
            return (
              <DemoWorkbenchInset
                key={g}
                compact
                className={`${color.border} ${color.bg}`}
                title={
                  <span className={`text-xs font-semibold ${color.text}`}>
                    {g} ({list.length})
                  </span>
                }
              >
                <DemoWorkbenchItemList
                  compact
                  maxItems={3}
                  items={list.map((bridge) => ({
                    id: bridge.bridge_id,
                    label: bridge.bridge_id,
                    mono: true,
                  }))}
                />
              </DemoWorkbenchInset>
            );
          })}
        </DemoWorkbenchFactGrid>
      </DemoWorkbenchCard>
    </DemoWorkbenchStack>
  );
}
